import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import firebaseInit from "../../firebase";
import { deleteObject, ref } from "firebase/storage";
import { logActivity } from "../firebaseActions/activityLog";
import { cpCampaign } from "./creditBonusCpActions";

const storage = firebaseInit.storage;
const fireStoreStorage = firebaseInit.firestore;

// ---------------------------------------------------------------------------
// CreateCampaign
// ---------------------------------------------------------------------------

export const CreateCampaign = async ({
  form,
  email = null,
  setIsLoading,
  setResponse,
  formRef,
  onSuccess,
}) => {
  try {
    const colRef = collection(fireStoreStorage, "credit_bonus_campaigns");
    const snap = await getDocs(colRef);
    const exists = snap.docs.some(
      (doc) => doc.data().promo_type === form.promo_type,
    );
    if (exists) {
      throw new Error(
        "Promo type must be unique. A campaign with this promo type already exists.",
      );
    }

    let agentBased = form.agent_based
      ? Array.isArray(form.agent_based)
        ? form.agent_based
        : [form.agent_based]
      : null;

    let promoType = form.promo_type.trim().replace(/\s+/g, "_");

    const docRef = await addDoc(
      collection(fireStoreStorage, "credit_bonus_campaigns"),
      {
        site: form.site,
        promo_type: promoType,
        promo_name: form.promo_name,
        promo_comment: form.promo_comment,
        credit_type: form.credit_type,
        status: form.status === 1 ? "active" : "inactive",
        restricted_countries: form.restricted_countries,
        allowed_countries: form.allowed_countries,
        start_date: form.start_date,
        end_date: form.end_date,
        credit_end_date: form.credit_end_date,
        promo_min_deposit: Number(form.promo_min_deposit),
        promo_max_credit: Number(form.promo_max_credit),
        // promo_timezone: form.promo_timezone,
        promo_timezone_gmt: form.promo_timezone_gmt,
        created_by: email ?? form.created_by,
        updated_by: email ?? form.updated_by,
        agent_based: agentBased,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
    );

    await cpCampaign(
      "create_campaign",
      {
        ...form,
        created_by: email ?? form.created_by,
        updated_by: email ?? form.updated_by,
        promo_type: promoType,
      },
      docRef.id,
      email ?? form.created_by,
      "cp_rollback",
    );

    setResponse("create_campaign", true);
    if (formRef?.current) formRef.current.reset();
    if (onSuccess) {
      onSuccess();

      //log
      logActivity({
        category: "cb_write_firestore",
        event: "cb_write_firestore_success",
        email: email,
        message: "Created campaign: " + form.promo_name,
      });
    }
  } catch (err) {
    console.error("CreateCampaign failed:", err);

    //log
    logActivity({
      category: "cb_write_firestore",
      event: "cb_write_firestore_failure",
      email: email,
      message:
        "Failed to create campaign: " +
        form.promo_name +
        " Error: " +
        err.message,
    });
    setResponse("create_campaign", false, err.message);
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// getCampaigns
// ---------------------------------------------------------------------------

export const getCampaigns = async ({ setIsLoading }) => {
  setIsLoading(true);
  try {
    const colRef = collection(fireStoreStorage, "credit_bonus_campaigns");
    const snapshot = await getDocs(colRef);

    if (snapshot.empty) return [];

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (err) {
    console.error("getCampaigns failed:", err);
    return [];
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// UpdateCampaign
// ---------------------------------------------------------------------------

export const UpdateCampaign = async ({
  email = null,
  form,
  setIsLoading,
  setResponse,
  onSuccess,
  formRef,
}) => {
  setIsLoading(true);
  try {
    const campaignRef = doc(
      fireStoreStorage,
      "credit_bonus_campaigns",
      form.id,
    );

    const snap = await getDoc(campaignRef);
    const existing = snap.data();

    const patch = {
      start_date: form.start_date,
      end_date: form.end_date,
      credit_end_date: form.credit_end_date,
      agent_based: form.agent_based.length > 0 ? form.agent_based : null,
      updated_by: email,
    };

    await updateDoc(campaignRef, {
      ...patch,
      updatedAt: serverTimestamp(),
    });

    await cpCampaign(
      "update_campaign",
      {
        ...patch,
        promo_type: form.promo_type,
      },
      form.id,
      email,
      "cp_rollback_update_campaign",
      () => updateDoc(campaignRef, { ...existing }),
    );

    setResponse("edit_campaign", true);
    if (formRef?.current) formRef.current.reset();
    if (onSuccess) {
      onSuccess();
      //log
      logActivity({
        category: "cb_update_firestore",
        event: "cb_update_campaign_success",
        email: email,
        metadata: { campaignId: form.id },
        message: "Updated: " + existing.promo_name,
      });
    }
  } catch (err) {
    console.error("UpdateCampaign failed:", err);
    setResponse("edit_campaign", false, err.message);
    //log
    logActivity({
      category: "cb_update_firestore",
      event: "cb_update_campaign_failure",
      email: email,
      metadata: { campaignId: form.id },
      message: "Failed to update campaign: " + err.message,
    });
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// UpdateCampaignAssets
// ---------------------------------------------------------------------------

// this is just for uploading assets
export const UpdateCampaignAssets = async ({
  campaignId,
  assetUrl,
  email = null,
  type = "",
  assetFileName,
  promoType,
  setIsLoading,
  setResponse,
  onSuccess,
}) => {
  let firestoreUpdated = false;
  try {
    // Validation: Ensure type is provided when uploading an asset
    if (type === "" || type === null) {
      throw new Error(
        "Asset type is required. Please select a valid type before uploading.",
      );
    }

    const campaignRef = doc(
      fireStoreStorage,
      "credit_bonus_campaigns",
      campaignId,
    );

    const snap = await getDoc(campaignRef);
    const existing = snap.data();

    const existingAssets = existing?.assets || [];

    // Add new asset to the list
    const newAsset = assetUrl
      ? {
          id: crypto.randomUUID(),
          url: assetUrl,
          type: type,
          fileName: assetFileName,
          addedBy: email,
        }
      : null;

    const assets = newAsset
      ? [...existingAssets.filter((a) => a.type !== type), newAsset]
      : existingAssets;

    await updateDoc(campaignRef, {
      updated_by: email,
      assets: assets,
      updatedAt: serverTimestamp(),
    });
    firestoreUpdated = true;

    await cpCampaign(
      "upload_assets",
      { ...[newAsset], added_by: email, promoType },
      email,
    );

    setResponse("update_campaign_assets", true);
    if (onSuccess) {
      onSuccess();
      //log
      logActivity({
        category: "cb_update_firestore",
        event: "cb_update_firestore_success",
        email: email,
        metadata: { campaignId: campaignId, assetFileName: assetFileName },
        message: "Updated (add assets): " + existing.promo_name,
      });
    }
  } catch (err) {
    console.error("UpdateCampaignAssets failed:", err);
    //cleanup storage if Firestore update fails

    if (firestoreUpdated) {
      await rollbackFirestoreAsset({ campaignId, assetFileName, email });
    }

    deleteStorageAsset(campaignId, assetFileName);

    setResponse("update_campaign_assets", false, err.message);
    //log
    logActivity({
      category: "cb_update_firestore",
      event: "cb_update_firestore_failure",
      email: email,
      metadata: { campaignId: campaignId, assetFileName: assetFileName },
      message: "Failed to update campaign (add assets): " + err.message,
    });
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// DeleteAssets
// ---------------------------------------------------------------------------

export const DeleteAssets = async ({
  campaignId,
  asset,
  email = null,
  setIsLoading,
  setResponse,
  onSuccess,
}) => {
  setIsLoading(true);
  try {
    const campaignRef = doc(
      fireStoreStorage,
      "credit_bonus_campaigns",
      campaignId,
    );
    const snap = await getDoc(campaignRef);
    const existing = snap.data();

    const updatedAssets = (existing?.assets || []).filter(
      (a) => a.id !== asset.id,
    );

    deleteStorageAsset(campaignId, asset.fileName);
    console.log("removed successfully - storage");
    logActivity({
      category: "cb_delete_assets",
      event: "cb_delete_assets_success",
      email: email,
      metadata: { campaignId: campaignId, assetFileName: asset.fileName },
      message:
        "Deleted asset: " +
        asset.fileName +
        " from campaign: " +
        existing.promo_name,
    });

    await updateDoc(campaignRef, {
      assets: updatedAssets,
      updated_by: email,
      updatedAt: serverTimestamp(),
    });
    logActivity({
      category: "cb_delete_assets_update",
      event: "cb_delete_assets_update_success",
      email: email,
      metadata: { campaignId: campaignId, assetFileName: asset.fileName },
      message:
        "Deleted asset ref: " +
        asset.fileName +
        " from campaign: " +
        existing.promo_name,
    });

    setResponse("update_campaign_assets", true);
    if (onSuccess) onSuccess();
  } catch (err) {
    console.error("DeleteAssets failed:", err);
    setResponse("update_campaign_assets", false);
    logActivity({
      category: "cb_delete_assets",
      event: "cb_delete_assets_failure",
      email: email,
      message:
        "Failed to delete asset: " + asset.fileName + " Error: " + err.message,
    });
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// DeleteCampaign
// ---------------------------------------------------------------------------

export const DeleteCampaign = async ({
  campaignId,
  email = null,
  logMsg = "cb_delete_campaign",
  setIsLoading,
  setResponse,
  onSuccess,
}) => {
  setIsLoading(true);
  try {
    const campaignRef = doc(
      fireStoreStorage,
      "credit_bonus_campaigns",
      campaignId,
    );

    const snap = await getDoc(campaignRef);
    const existing = snap.data();

    if (existing?.assets?.length > 0) {
      const storageRef = ref(storage, `credit_bonus/${campaignId}`);
      await deleteObject(storageRef);
      logActivity({
        category: "cb_delete_campaign_assets",
        event: "cb_delete_campaign_assets_success",
        email: email,
        metadata: { campaignId: campaignId },
        message: "Deleted campaign assets for campaign: " + existing.promo_name,
      });
    }

    await deleteDoc(campaignRef);

    logActivity({
      category: logMsg,
      event: `${logMsg}_success`,
      email: email,
      metadata: { campaignId: campaignId },
      message: "Deleted campaign: " + campaignId,
    });

    setResponse("delete_campaign", true);
    if (onSuccess) onSuccess();
    return true;
  } catch (err) {
    console.error("DeleteCampaign failed:", err);
    setResponse("delete_campaign", false);
    logActivity({
      category: logMsg,
      event: `${logMsg}_failure`,
      email: email,
      message:
        "Failed to delete campaign: " + campaignId + " Error: " + err.message,
    });
  } finally {
    setIsLoading(false);
  }
};

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

export const deleteStorageAsset = async (campaignId, fileName) => {
  try {
    const storageRef = ref(storage, `credit_bonus/${campaignId}/${fileName}`);
    await deleteObject(storageRef);

    console.log("removed successfully - storage");

    logActivity({
      category: "cb_rollback_storage",
      event: "cb_rollback_storage_success",
      metadata: { campaignId, fileName },
      message: "Deleted asset from storage: " + fileName,
    });
  } catch (err) {
    console.error("Failed to delete asset from storage:", err);
    logActivity({
      category: "cb_rollback_storage",
      event: "cb_rollback_storage_failure",
      metadata: { campaignId, fileName },
      message: "Failed to delete asset from storage: " + err.message,
    });
  }
};

const rollbackFirestoreAsset = async ({ campaignId, assetFileName, email }) => {
  try {
    const campaignRef = doc(
      fireStoreStorage,
      "credit_bonus_campaigns",
      campaignId,
    );
    const snap = await getDoc(campaignRef);
    const rolledBackAssets = (snap.data()?.assets || []).filter(
      (a) => a.fileName !== assetFileName,
    );
    await updateDoc(campaignRef, {
      assets: rolledBackAssets,
      updatedAt: serverTimestamp(),
    });
    logActivity({
      category: "cb_rollback_update_firestore",
      event: "cb_rollback_success",
      email,
      metadata: { campaignId, assetFileName },
      message: "Firestore rollback succeeded — asset reference removed.",
    });
  } catch (err) {
    console.error(
      "CRITICAL: Firestore rollback failed. Manual cleanup needed.",
      {
        campaignId,
        assetFileName,
        err,
      },
    );
    logActivity({
      category: "cb_rollback_update_firestore",
      event: "cb_rollback_failure",
      email,
      metadata: { campaignId, assetFileName },
      message:
        "Firestore rollback failed — storage and Firestore may be out of sync.",
    });
  }
};
